import Select, { type GroupBase, type Props } from 'react-select';

import { useChartTheme } from '@revelio/replots';

type StyledPlotSelectProps = { maxWidth?: number; height?: string };

export const StyledPlotSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  maxWidth,
  height,
  ...props
}: Props<Option, IsMulti, Group> & StyledPlotSelectProps) => {
  // Themed (light AND dark) restyles via flip-per-theme tokens — the v1.1
  // chip control + floating menu; legacy keeps the original literals.
  const { isNewTheme: themed } = useChartTheme();
  return (
    <Select
      classNamePrefix={props.classNamePrefix}
      isSearchable={props.isSearchable ?? false}
      styles={{
        container: (prev) => ({
          ...prev,
          ...(maxWidth
            ? { maxWidth: `${maxWidth}px`, width: '100%' }
            : { width: '100%' }),
          ...(height ? { height } : {}),
          fontSize: '12px',
          fontWeight: 400,
        }),
        control: (prev) => ({
          ...prev,
          minHeight: height || '24px',
          height: '100%',
          backgroundColor: themed ? 'rgb(var(--ui-ink-rgb) / 0.02)' : '#F1F5F9',
          color: themed ? 'var(--ui-text-normal)' : prev.color,
          ...(themed
            ? {
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'rgb(var(--ui-ink-rgb) / 0.08)',
                borderRadius: 2,
              }
            : { borderWidth: 0, borderColor: 'none' }),
          boxShadow: 'none',
        }),
        valueContainer: (prev) => ({
          ...prev,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          overflow: 'hidden',
        }),
        placeholder: (prev) => ({
          ...prev,
          margin: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          pointerEvents: 'none',
          ...(themed ? { color: 'var(--ui-text-secondary)' } : {}),
        }),
        indicatorsContainer: (prev) => ({
          ...prev,
          height: '100%',
          svg: { width: '12px', height: '12px' },
        }),
        clearIndicator: (prev) => ({
          ...prev,
          color: themed ? 'var(--ui-text-secondary)' : '#A0AEC0',
          padding: '4px',
          cursor: 'pointer',
          ' svg': {},
          ':hover': {
            color: themed ? 'var(--ui-text-normal)' : '#718096',
          },
        }),
        indicatorSeparator: (prev) => ({
          ...prev,
          display: 'none',
        }),
        dropdownIndicator: (prev) => ({
          ...prev,
          color: themed ? 'var(--ui-text-secondary)' : '#2D426A',
          ' svg': {
            width: '12px',
            height: '12px',
          },
        }),
        menu: (prev) => ({
          ...prev,
          borderRadius: 0,
          ...(maxWidth
            ? { minWidth: `${maxWidth}px` }
            : { width: 'fit-content', minWidth: '100px' }),
          position: 'absolute',
          left: 0,
          right: 0,
          zIndex: 3,
          ...(themed
            ? {
                backgroundColor: 'var(--ui-surface-floating)',
                border: '1px solid var(--ui-border-floating)',
              }
            : {}),
        }),
        group: (prev) => ({
          ...prev,
          paddingBottom: 0,
          marginBottom: 0,
        }),
        groupHeading: (prev) => ({
          ...prev,
          padding: '0 .5rem',
          ...(themed ? { color: 'var(--ui-text-muted)' } : {}),
        }),
        option: (prev, { isSelected, isFocused }) => {
          const backgroundColor = (() => {
            if (isSelected)
              return themed ? 'var(--ui-surface-overlay)' : '#3182ce';
            if (isFocused)
              return themed ? 'var(--ui-surface-hover)' : '#EDF2F7';
            return 'transparent';
          })();
          const optionColor = (() => {
            if (isSelected) return themed ? 'var(--ui-text-normal)' : '#fff';
            return themed ? 'var(--ui-text-normal)' : '#2D426A';
          })();
          return {
            ...prev,
            backgroundColor,
            color: optionColor,
            fontSize: '12px',
            fontWeight: 400,
            padding: '8px 12px',
            minHeight: '32px',
            lineHeight: '16px',
            cursor: 'pointer',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            ...(isSelected && {
              ':hover': {
                backgroundColor: themed
                  ? 'var(--ui-surface-active)'
                  : '#2970b2',
              },
            }),
          };
        },
        singleValue: (prev) => ({
          ...prev,
          height: '100%',
          lineHeight: '100%',
          ...(themed ? { color: 'var(--ui-text-normal)' } : {}),
        }),
        multiValue: (prev) => ({
          ...prev,
          fontSize: '11px',
          margin: '2px 4px 2px 0',
          ...(themed
            ? { backgroundColor: 'rgb(var(--ui-ink-rgb) / 0.05)' }
            : {}),
        }),
        multiValueLabel: (prev) => ({
          ...prev,
          fontSize: '11px',
          padding: '4px',
          lineHeight: '1.1',
          ...(themed ? { color: 'var(--ui-text-normal)' } : {}),
        }),
        multiValueRemove: (prev) => ({
          ...prev,
          fontSize: '11px',
          padding: '2px 4px',
        }),
        input: (prev) => ({
          ...prev,
          height: '20px',
          margin: 0,
          padding: 0,
          ...(themed ? { color: 'var(--ui-text-normal)' } : {}),
          '> .metric-switch__input': {
            height: '20px',
          },
        }),
      }}
      {...props}
    />
  );
};
